import React, { useState, useRef, forwardRef, useImperativeHandle, ReactNode, useEffect } from 'react';
import * as yup from 'yup';
import { useSynapse } from 'app/lib/synaptik';
import {
  styled,
  css,
  Select,
  Row,
  Column,
  Box,
  Input,
  FormError,
  Form,
  Formbot,
  Label,
  Text,
  Flex,
  Spinner,
} from '@goodrx/matisse';

import { get, pick } from 'lodash';
import Geosuggest from 'react-geosuggest';
import { AddressData } from '@goodrx/heydoctor-sdk';
import heydoctor from 'app/vendor/heydoctor';
import { states as stateOptions } from 'app/data';
import { AddressFormHandlers, Address } from 'app/types';

const AddressLineContainer = styled.div<{ invalidAddress: boolean }>`
  margin-bottom: 16px;
  ${(p) =>
    p.invalidAddress &&
    `
    label {
      color: ${p.theme.colors.red};
    }
    input,
    input:focus {
      border-color: ${p.theme.colors.red};
    }
  `}
`;

const ErrorContainer = styled(Box)`
  color: ${(p) => p.theme.colors.red};
  font-size: 14px;
  font-weight: 500;
  margin-top: 12px;
`;

const GeoInputContainer = styled.div(
  (p) => css`
    position: relative;
    margin-bottom: 16px;

    .geosuggest__input {
      border: 1px solid ${p.theme.colors.greyLight};
      border-radius: 8px;
      font-size: 16px;
      height: 48px;
      display: block;
      outline: none;
      width: 100%;
      transition: 250ms all;
      font-weight: 500;
      -webkit-appearance: none;

      &:hover,
      &:active {
        border-color: ${p.theme.colors.greyDarker};
      }

      &:focus {
        border-color: ${p.theme.colors.primary};
      }

      &::placeholder {
        color: ${p.theme.colors.greyDarker};
        font-weight: 500;
      }
    }

    .geosuggest {
      position: relative;

      &.is-blurred {
        .geosuggest__suggests-wrapper {
          display: none;
        }
      }
    }

    .geosuggest__suggests {
      background: white;
      width: 100%;
      position: absolute;
      top: 100%;
      z-index: 10;
      box-shadow: 0 2px 4px ${p.theme.colors.grey};
      border-radius: 4px;
      padding: 0;
      margin-top: 8px;
    }

    .geosuggest__suggests--hidden {
      display: none;
    }

    .geosuggest__item {
      padding: 12px;
      cursor: pointer;
      list-style: none;

      & + & {
        border-top: ${p.theme.colors.greyLight};
      }

      &--active,
      &:hover {
        background: ${p.theme.colors.greyLight};
      }
    }
  `
);

const GeosuggestWithLabel: any = styled(Geosuggest)`
  ${(p: any) => css`
    input[disabled] {
      background-color: ${p.theme.colors.white};
      border-color: ${p.theme.colors.greyLight};
      color: ${p.theme.colors.grey};
    }
    input {
      padding: 12px 8px;
      font-family: inherit;

      ${p.hasValue &&
      css`
        padding-bottom: 0;
        padding-top: 14px;
        color: ${p.theme.colors.black};
      `}
    }
  `}
`;

async function getStateFromZip(zip: any) {
  try {
    const data = await heydoctor.address.geocodeZip(zip);

    return get(data, 'state.shortName');
  } catch (err) {
    const message = get(err, 'response.data.message') || 'Could not validate zip code.';
    throw new Error(message);
  }
}

export type AddressFields = keyof Address;

const ErrorMessages = {
  INVALID_STATE:
    'Unfortunately we are unable to provide you care in the state you selected. ' +
    "We're working as quickly as we can to have nationwide coverage.",
  MISMATCHED_ZIP: 'Zip code must be in the state you have selected.',
};

export const ADDRESS_FIELDS = ['addressLine1', 'addressLine2', 'city', 'state', 'zip'];

export type FormValues = Partial<Omit<AddressData, 'phone'>>;

type AddressFormProps = {
  address: Address;
  disabledFields?: AddressFields[];
  onSubmit?: (data: any) => void;
  children?: ReactNode;
  onChange?: (...args: any) => void;
};

interface FormRenderProps {
  values: FormValues;
  errors: FormValues;
  onChange: (field: string, value: boolean | string | null) => Record<string, unknown>;
}

export const AddressForm = forwardRef<AddressFormHandlers, AddressFormProps>(
  ({ address, onSubmit, onChange: handleChange, disabledFields = [], children }, ref) => {
    const initialAddress = {
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      ...address,
    };

    const [states] = useSynapse(({ business }) => [business.state.states]);

    const [addressLine1Focused, setAddressLine1Focused] = useState(false);
    const [mapsApiLoaded, setMapsApiLoaded] = useState(false);

    const formbot = useRef<Formbot>(null);
    const geosuggest: any = useRef();

    const setValues = async (values: FormValues) => formbot.current?.setValues(pick(values, ADDRESS_FIELDS));
    const setErrors = (errors: FormValues) => formbot.current?.setErrors(errors, null);

    const reset = () => formbot.current?.reset();

    useEffect(() => {
      // eslint-disable-next-line no-undef
      if (typeof google !== 'undefined' && google.maps) {
        setMapsApiLoaded(true);
        return;
      }

      const script = document.createElement('script');

      script.src =
        'https://maps.googleapis.com/maps/api/js?key=AIzaSyDHkqY8ytSQCfeQP6PlKgza3XJsO4SBCDE&libraries=places';
      script.id = 'google.maps';
      script.async = true;
      script.onload = () => setMapsApiLoaded(true);
      script.onerror = () => setMapsApiLoaded(true);

      document.body.appendChild(script);
    }, []);

    useImperativeHandle(ref, () => ({
      get isValid() {
        return !!formbot.current?.isValid;
      },
      async getValues() {
        const result = await formbot.current?.getValues();
        const { values = false, isValid = false } = result || {};
        return { values: pick(values, ADDRESS_FIELDS), isValid };
      },
      setValues,
      setErrors,
      reset,
    }));

    async function onSuggestSelect(suggestion: any = {}) {
      if (!suggestion.gmaps) return;
      /* eslint-disable camelcase */
      const suggestedAddress = suggestion.gmaps.address_components.reduce(
        // eslint-disable-next-line @typescript-eslint/naming-convention
        (addr: any, { types, long_name, short_name }: any) => {
          if (types.includes('street_number')) {
            addr.addressLine1 = addr.addressLine1 ? `${long_name} ${addr.addressLine1}` : long_name;
          }
          if (types.includes('route')) {
            addr.addressLine1 = addr.addressLine1 ? `${addr.addressLine1} ${long_name}` : long_name;
          }
          if (types.includes('locality')) {
            addr.city = long_name;
          }
          if (types.includes('administrative_area_level_1')) {
            addr.state = short_name;
          }
          if (types.includes('postal_code')) {
            addr.zip = short_name;
          }
          return addr;
        },
        {}
      );
      /* eslint-enable camelcase */

      await formbot.current?.setValues(suggestedAddress);
      geosuggest.current.update(suggestedAddress.addressLine1 || '');
    }

    if (!mapsApiLoaded) {
      return (
        <Flex justifyContent="center">
          <Spinner />
        </Flex>
      );
    }

    return (
      <Formbot
        ref={formbot}
        validationSchema={{
          addressLine1: yup.string().required('Please enter the first line of your address.'),
          city: yup.string().ensure().required('Please enter the name of your city.'),
          state: yup.string().required('Please select a state.'),
          zip: yup
            .string()
            .ensure()
            .min(5, 'Please enter a 5 digit zip code')
            .required('A valid zip code is required.')
            .test('valid', ErrorMessages.MISMATCHED_ZIP, async function testZipCode(this: any, zip: any) {
              const {
                options: {
                  context: { values }, // Formbot context
                },
              } = this;

              if (!!zip && zip.length === 5) {
                const stateFromZip = await getStateFromZip(zip);
                return stateFromZip === values.state;
              }

              return false;
            }),
        }}
        initialValues={initialAddress}
        onChange={(_fieldName: string, _fieldValue: any, stuff: Address) => {
          if (typeof handleChange === 'function') handleChange(stuff);
        }}
        onSubmit={onSubmit}>
        {({ values: { addressLine1, addressLine2, city, state, zip }, onChange, errors }: FormRenderProps) => {
          return (
            <Form>
              <GeoInputContainer>
                <GeosuggestWithLabel
                  name="addressLine1"
                  label="Address Line 1"
                  disabled={disabledFields.includes('addressLine1')}
                  ref={geosuggest}
                  placeholder="Street and number, P.O. box, c/o."
                  initialValue={addressLine1 || ''}
                  onSuggestSelect={(suggestion: any) => {
                    onSuggestSelect(suggestion);
                    setAddressLine1Focused(false);
                  }}
                  className={addressLine1Focused ? 'is-focused' : 'is-blurred'}
                  onChange={(value: any) => {
                    onChange('addressLine1', value);
                  }}
                  onFocus={() => setAddressLine1Focused(true)}
                  onBlur={() => setAddressLine1Focused(false)}
                  autoComplete={addressLine1Focused ? 'nope' : 'on'}
                  country="us"
                  hasValue={!!addressLine1}
                  queryDelay={1250}
                />
                {!!addressLine1 && (
                  <Label
                    isDisabled={disabledFields.includes('addressLine1')}
                    isFloatable
                    isFloating
                    isFocused={addressLine1Focused}>
                    Address Line 1
                  </Label>
                )}
                <FormError name="addressLine1" />
              </GeoInputContainer>
              <AddressLineContainer invalidAddress={!!errors.addressLine2}>
                <Input
                  value={addressLine2 || ''}
                  name="addressLine2"
                  disabled={disabledFields.includes('addressLine2')}
                  floating
                  label="Address Line 2"
                  placeholder="Apartment, suite, unit, building, floor, etc."
                />
              </AddressLineContainer>

              <Input
                disabled={disabledFields.includes('city')}
                value={city || ''}
                name="city"
                label="City"
                floating
                placeholder="City"
              />

              <Row>
                <Column sm={6} xs={12} pt={3}>
                  <Select
                    onChange={onChange}
                    value={state || ''}
                    name="state"
                    disabled={disabledFields.includes('state')}
                    id="state"
                    label="State"
                    floating
                    options={stateOptions}
                    placeholder="State"
                    error={null} // Since we handle errors below, this will suppress duplicating error message
                  />
                </Column>
                <Column sm={6} xs={12} pt={3}>
                  <Input
                    value={zip || ''}
                    type="tel"
                    name="zip"
                    disabled={disabledFields.includes('zip')}
                    label="Zip Code"
                    floating
                    placeholder="Zip"
                    maxLength={5}
                  />
                </Column>
              </Row>

              <FormError name="state">
                {(error: any) => (
                  <>
                    <ErrorContainer>{error}</ErrorContainer>

                    <Box mt={3}>
                      <Text color="greyDarkest">
                        We&apos;re currently available in the following states: <br />
                        {states.map((s: any) => s.code).join(', ')}
                      </Text>
                    </Box>
                  </>
                )}
              </FormError>

              {children}
            </Form>
          );
        }}
      </Formbot>
    );
  }
);
